import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Menu, Zap, Sun, Moon } from 'lucide-react';
import api from '../services/api';
import NotificationBell from './NotificationBell';
import { useTheme } from '../hooks/useTheme';

const Header = ({ toggleSidebar, isCollapsed, pageTitle, userRole }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const { isDark, toggleTheme } = useTheme();

  // ── Admin Automation State ──────────────────────────────────────────────────
  const [actionLoading, setActionLoading] = useState({ invoice: false, alert: false });
  const [actionMessage, setActionMessage] = useState('');
  const [actionMessageType, setActionMessageType] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showAutomationMenu, setShowAutomationMenu] = useState(false);

  useEffect(() => {
    // Django SimpleJWT only stores user_id in the token by default — NOT username.
    // Fetching from the API is the reliable approach.
    api.get('/users/me/')
      .then(res => {
        const d = res.data;
        setUsername(d.username || d.name || d.full_name || d.email || 'User');
      })
      .catch(() => {
        // Graceful fallback: decode JWT and try any name-like field
        try {
          const token = localStorage.getItem('access_token');
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUsername(payload.username || payload.name || payload.sub || 'User');
        } catch {
          setUsername('User');
        }
      });
  }, []);

  // ── Cooldown Timer ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleGenerateInvoices = async () => {
    setActionLoading({ ...actionLoading, invoice: true });
    setActionMessage('');
    setActionMessageType('');
    try {
      const res = await api.post('/admin/trigger-invoices/');
      setActionMessage(`✅ ${res.data.message}`);
      setActionMessageType('success');
    } catch (err) {
      setActionMessage(`❌ Error: ${err.response?.data?.message || err.message}`);
      setActionMessageType('error');
    } finally {
      setActionLoading({ ...actionLoading, invoice: false });
    }
  };

  const handleSendAlerts = async () => {
    setActionLoading({ ...actionLoading, alert: true });
    setActionMessage('');
    setActionMessageType('');
    try {
      const res = await api.post('/admin/trigger-alerts/');
      const result = res.data;
      if (result.status === 'cooldown') {
        setActionMessage(`⏳ ${result.message}`);
        setActionMessageType('cooldown');
        setCooldownSeconds(result.remaining_seconds || 0);
      } else {
        setActionMessage(`✅ ${result.message}`);
        setActionMessageType('success');
        setCooldownSeconds(0);
      }
    } catch (err) {
      setActionMessage(`❌ Error: ${err.response?.data?.message || err.message}`);
      setActionMessageType('error');
    } finally {
      setActionLoading({ ...actionLoading, alert: false });
    }
  };

  // Auto-clear action messages after 5s
  useEffect(() => {
    if (!actionMessage) return;
    const t = setTimeout(() => setActionMessage(''), 5000);
    return () => clearTimeout(t);
  }, [actionMessage]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate('/login');
  };

  const initial = username ? username[0].toUpperCase() : '?';

  return (
    <header
      className={`
        bg-white h-16 border-b border-gray-200 shadow-sm
        fixed top-0 right-0 left-0 z-40
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'lg:left-20' : 'lg:left-64'}
      `}
    >
      <div className="flex items-center justify-between h-full px-4 sm:px-6">

        {/* Left: mobile hamburger + page title */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors lg:hidden"
          >
            <Menu size={22} />
          </button>
          <h2 className="text-sm sm:text-xl font-bold text-gray-800 tracking-tight truncate whitespace-nowrap">
            {pageTitle || 'Dashboard'}
          </h2>
        </div>

        {/* Right: automation (admin) + notification bell + username chip + logout */}
        <div className="flex items-center gap-1.5 sm:gap-2">

          {/* Admin Automation Dropdown */}
          {userRole === 'admin' && (
            <div className="relative">
              <button
                onClick={() => setShowAutomationMenu(!showAutomationMenu)}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-700 transition-all text-sm font-medium"
                title="Admin Automation Controls"
              >
                <Zap size={16} />
                <span className="hidden md:inline">Automation</span>
              </button>

              {showAutomationMenu && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowAutomationMenu(false)}
                  />
                  <div className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-full mt-0 sm:mt-2 w-[calc(100vw-16px)] sm:w-80 max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 z-50 p-4">
                    <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-600" /> Admin Automation Controls
                    </h3>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleGenerateInvoices}
                        disabled={actionLoading.invoice}
                        className="w-full px-4 py-2.5 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-all text-sm font-medium"
                        style={{ background: 'var(--brand)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-dark)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--brand)'}
                      >
                        {actionLoading.invoice ? '⏳ Generating...' : '💰 Generate All Invoices'}
                      </button>
                      <button
                        onClick={handleSendAlerts}
                        disabled={actionLoading.alert || cooldownSeconds > 0}
                        className={`w-full px-4 py-2.5 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-all text-sm font-medium ${
                          cooldownSeconds > 0 ? 'bg-amber-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {actionLoading.alert ? '⏳ Sending...' : cooldownSeconds > 0 ? `⏳ Wait ${formatTime(cooldownSeconds)}` : '📱 Send Expiry Alerts'}
                      </button>
                    </div>

                    {actionMessage && (
                      <div className={`mt-3 p-2 rounded-lg text-xs ${
                        actionMessageType === 'error' ? 'bg-red-50 text-red-700' :
                        actionMessageType === 'cooldown' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                        'bg-green-50 text-green-700'
                      }`}>
                        {actionMessage}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <NotificationBell />

          {/* Dark / Light mode toggle */}
          <button
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-700 transition-colors"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <Link
            to="/profile"
            className="flex items-center gap-2.5 px-2 sm:px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-[#F3E5F5] hover:border-[#E1BEE7] transition-all group"
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #9C27B0, #6A1B9A)' }}>
              {initial}
            </div>
            <span className="text-sm font-semibold text-gray-800 group-hover:text-[#7B1FA2] transition-colors max-w-[150px] truncate hidden sm:inline">
              {username || '…'}
            </span>
          </Link>

          <button
            onClick={handleLogout}
            title="Logout"
            className="flex items-center gap-2 px-2 sm:px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 transition-all"
          >
            <LogOut size={16} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
